import { REMINDER_TAG_PREFIX, ReminderAction, type ReminderCommand } from './reminderProtocol';

/** What the browser allows for system notifications on this site. */
export const NotifyPermission = {
  Unsupported: 'unsupported',
  Default: 'default',
  Granted: 'granted',
  Denied: 'denied',
} as const;
export type NotifyPermission = (typeof NotifyPermission)[keyof typeof NotifyPermission];

const notificationApi = (): typeof Notification | undefined =>
  typeof window !== 'undefined' && 'Notification' in window ? window.Notification : undefined;

export function notifyPermission(): NotifyPermission {
  const api = notificationApi();
  if (api === undefined) return NotifyPermission.Unsupported;
  return api.permission === 'granted'
    ? NotifyPermission.Granted
    : api.permission === 'denied'
      ? NotifyPermission.Denied
      : NotifyPermission.Default;
}

/** Ask for permission (needs a user gesture); returns the resulting state. */
export async function requestNotifyPermission(): Promise<NotifyPermission> {
  const api = notificationApi();
  if (api === undefined) return NotifyPermission.Unsupported;
  try {
    await api.requestPermission();
  } catch {
    // Older Safari only supports the callback form; the state below is still accurate.
  }
  return notifyPermission();
}

export interface NoticeButton {
  readonly action: typeof ReminderAction.Done | typeof ReminderAction.Snooze;
  readonly title: string;
}

export interface ReminderNotice {
  readonly title: string;
  readonly body: string;
  /** Unique per reminder group: a second tab showing the same reminder replaces it instead of adding one. */
  readonly tagSuffix: string;
  readonly keys: readonly string[];
  readonly buttons: readonly NoticeButton[];
}

const ICON = 'icon-192.png';
/** How long to wait for an active service worker before falling back to a page notification. */
const WORKER_READY_TIMEOUT_MS = 3_000;

/** The registration with an active worker, or undefined when none becomes ready in time (no worker in development). */
async function activeRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  const container = navigator.serviceWorker as ServiceWorkerContainer | undefined;
  if (container === undefined || (await container.getRegistration()) === undefined) return undefined;
  let timer = 0;
  const timeout = new Promise<undefined>((resolve) => {
    timer = window.setTimeout(() => resolve(undefined), WORKER_READY_TIMEOUT_MS);
  });
  try {
    return await Promise.race([container.ready, timeout]);
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Show a reminder as a system notification. Through the service worker when one controls the
 * page, so the buttons work (Chromium) and a click reaches the page by message; otherwise a plain
 * page notification whose click calls `onFallbackClick` (no buttons there). Returns false when
 * notifications are not allowed or showing failed.
 */
export async function showReminderNotice(
  notice: ReminderNotice,
  onFallbackClick: (command: ReminderCommand) => void,
): Promise<boolean> {
  const api = notificationApi();
  if (api === undefined || notifyPermission() !== NotifyPermission.Granted) return false;
  const tag = `${REMINDER_TAG_PREFIX}-${notice.tagSuffix}`;
  try {
    const registration = await activeRegistration();
    if (registration !== undefined) {
      // `actions` is not in every TypeScript DOM library version; browsers without it ignore the field.
      const options = {
        body: notice.body,
        tag,
        // A reminder shown again under its tag (a snooze, a second summary) alerts again.
        renotify: true,
        icon: ICON,
        data: { keys: notice.keys },
        actions: notice.buttons.map((button) => ({ action: button.action, title: button.title })),
      } as NotificationOptions;
      await registration.showNotification(notice.title, options);
      return true;
    }
    const notification = new api(notice.title, { body: notice.body, tag, icon: ICON, renotify: true } as NotificationOptions);
    notification.onclick = () => {
      window.focus();
      notification.close();
      onFallbackClick({ action: ReminderAction.Open, keys: notice.keys });
    };
    return true;
  } catch {
    return false;
  }
}
